import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import { Clock, User } from "lucide-react"
import PublicLayout from "@/components/PublicLayout"
import { NewsletterSignup } from "@/components/gisela/NewsletterSignup"
import { CategoryTag } from "@/components/ui/category-tag"
import { useLocalePath } from "@/lib/locale-path"
import type { PageProps } from "@/types/inertia"

interface Article {
  id: number
  slug: string
  title: string
  body: string | null
  excerpt: string | null
  category: string
  author: string
  read_time: number | null
  thumbnail: string | null
  /** Cover photo at article width; null until one is generated or picked. */
  photo_lead_url: string | null
  /** The difficulty this piece was written for — the blog filters on it. */
  topic_slug: string | null
  published_at: string | null
}

interface Props {
  article: Article
  /** True when a signed-in admin is looking at a draft — nobody else can. */
  preview?: boolean
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
}

export default function BlogPostShow({ article, preview = false }: Props) {
  const { translations, features, current_brand } = usePage<PageProps>().props
  const t = translations.blog.show
  const tc = translations.common
  // The blog's canonical home is Gisela's domain, so the site name in the tab,
  // the search result and the social card has to follow the host — hardcoding
  // the venue's name branded every one of her articles with it.
  const brandName = current_brand.name
  const lp = useLocalePath()
  // Articles link to condition pages and to each other by English path; keep
  // the reader on their own language, as ConditionPage and TherapyPage do.
  const markdown: Components = {
    a: ({ href, children }) => <a href={lp(href ?? "")}>{children}</a>,
  }

  return (
    <>
      <Head title={`${article.title} — ${brandName}`}>
        <meta
          name="description"
          content={article.excerpt ?? `${article.title} — read on ${brandName}.`}
        />
        <meta property="og:title" content={`${article.title} — ${brandName}`} />
        <meta
          property="og:description"
          content={article.excerpt ?? `${article.title} — read on ${brandName}.`}
        />
        {/* The cover doubles as the social card. Without it a shared article
            is a bare grey box in Slack, WhatsApp and LinkedIn — these were the
            first images the blog has ever had to offer. */}
        {article.photo_lead_url && (
          <>
            <meta property="og:image" content={article.photo_lead_url} />
            <meta property="og:type" content="article" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={article.photo_lead_url} />
          </>
        )}
        {preview && <meta name="robots" content="noindex" />}
      </Head>
      <PublicLayout>
        {/* Article header */}
        <section className="bg-stone pt-32 pb-12">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            {preview && (
              <p
                role="status"
                className="mb-6 rounded-lg border border-signal/40 bg-signal/10 px-4 py-3 text-sm text-ink-body"
              >
                <strong>Draft preview.</strong> Only signed-in admins can see this page; it is not on the
                blog until the article is set to Published.
              </p>
            )}
            <CategoryTag label={article.category} />
            <h1 className="mt-4">{article.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-ink-muted">
              {article.author && (
                <span className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {article.author}
                </span>
              )}
              {article.read_time && (
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {tc.min_read.replace("%{count}", String(article.read_time))}
                </span>
              )}
              {article.published_at && <span>{formatDate(article.published_at)}</span>}
            </div>
            {article.excerpt && (
              <p className="mt-5 text-base leading-relaxed text-ink-muted border-l-2 border-sage pl-4">
                {article.excerpt}
              </p>
            )}
            {/* The same cover the reader clicked in the grid, at the width of
                the text it belongs to. Cropped to 16:9 rather than shown at
                its native 4:3: a 768px column would otherwise open on a 576px
                slab of picture and push the first paragraph off the screen.
                Decorative — the piece is titled and described directly above
                it — so the alt text stays empty rather than repeating the
                headline to a screen reader that has just read it. */}
            {article.photo_lead_url && (
              <img
                src={article.photo_lead_url}
                alt=""
                className="mt-8 aspect-[16/9] w-full rounded-lg object-cover"
              />
            )}
          </div>
        </section>

        {/* Article body */}
        <section className="bg-cream py-12">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            {article.body ? (
              <div className="body-content prose max-w-none text-ink-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdown}>
                  {article.body}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-ink-muted">{t.content_coming_soon}</p>
            )}
            {features.newsletter_signup && (
              <div className="mt-12">
                <NewsletterSignup variant="card" />
              </div>
            )}
            {/* Back into the library, and — the other half of the cluster —
                straight to everything else written for the same difficulty. */}
            <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-hairline pt-8">
              <a href={lp("/blog")} className="text-sm font-medium text-teal-mid hover:text-teal">
                {t.back_link}
              </a>
              {article.topic_slug && article.category && (
                <a
                  href={lp(`/blog?topic=${encodeURIComponent(article.topic_slug)}`)}
                  className="text-sm font-medium text-teal-mid hover:text-teal"
                >
                  {t.more_on_topic.replace("%{name}", article.category)}
                </a>
              )}
            </div>
          </div>
        </section>
      </PublicLayout>
    </>
  )
}
